import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useToggle } from '../hooks/useToggle';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { CardDate } from '../components/CardDate';
import editOn from '../assets/editOn.svg';
import trashSVG from '../assets/trash.svg';
import { Category, CategoryId, CategoryPublication } from '../api/category';
import Spinner from '../spinner/Spinner';
import { CategoryPublicationCard } from './CategoryPublicationCard';
import { CategoryPublicationForm } from './PublicationFormCategory';
import { IconButton } from '../components/Button';
import addCircleIcon from '../assets/add-circle.svg';
import { Option } from '../types/option';
import { imageSrc } from '../api/media';

type Props = {
    category: Category;
    publications: Option<CategoryPublication[]>;
    loadingCategoryPublication: boolean;
    publicationsLoadError: Option<string>;
    onUpdate: (c: Category) => void;
    onArchive: (c: Category) => void;
    onLoadPublications: (id: CategoryId) => void;
    onRefreshPublications: (id: CategoryId) => void;
};

export const CategoryCard = memo(
    ({
        category,
        publications,
        loadingCategoryPublication,
        publicationsLoadError,
        onUpdate,
        onArchive,
        onLoadPublications,
        onRefreshPublications,
    }: Props) => {
        const [isOpen, setIsOpen] = useToggle(false);
        const [isCreatePublication, setIsCreatePublication] = useState<boolean>(false);
        const onLoadRef = useRef(onLoadPublications);
        onLoadRef.current = onLoadPublications;
        const mediaUrl = category.media
            .filter(media => media.url.trim() !== '')
            .map(media => imageSrc(media));

        const { attributes, listeners, setNodeRef, transform, isDragging } = useSortable({
            id: category.id,
        });

        useEffect(() => {
            if (isOpen) {
                onLoadRef.current(category.id);
            }
        }, [isOpen, category.id]);

        const handleToggle = useCallback(() => {
            setIsOpen(prev => {
                if (prev) {
                    setIsCreatePublication(false);
                }
                return !prev;
            });
        }, [setIsOpen, setIsCreatePublication]);

        return (
            <li
                ref={setNodeRef}
                {...attributes}
                style={{
                    transform: CSS.Transform.toString(transform),
                    transition: isDragging ? 'none' : 'transform 200ms ease',
                }}
                className={`relative flex h-auto flex-col overflow-hidden rounded-md bg-white ease-in-out ${
                    isDragging ? 'shadow-xl' : 'shadow-md'
                }`}
            >
                <div
                    className="flex cursor-pointer select-none items-center justify-between px-8 py-4"
                    onClick={handleToggle}
                >
                    <div className="flex w-full items-center gap-2">
                        <button
                            type="button"
                            {...listeners}
                            aria-roledescription="draggable"
                            onClick={e => e.stopPropagation()}
                            className="mr-4 grid cursor-grab grid-cols-2 justify-center gap-1"
                        >
                            <span className="block h-1 w-1 rounded-full bg-dark-gray" />
                            <span className="block h-1 w-1 rounded-full bg-dark-gray" />
                            <span className="block h-1 w-1 rounded-full bg-dark-gray" />
                            <span className="block h-1 w-1 rounded-full bg-dark-gray" />
                            <span className="block h-1 w-1 rounded-full bg-dark-gray" />
                            <span className="block h-1 w-1 rounded-full bg-dark-gray" />
                        </button>
                        {mediaUrl
                            .map(url => (
                                <div className="h-[60px] w-[85px] shrink-0 overflow-hidden rounded-[12px] bg-light-gray">
                                    <img
                                        src={url}
                                        alt={category.title}
                                        className="block h-full w-full object-cover object-center"
                                        loading="lazy"
                                    />
                                </div>
                            ))
                            .toNull()}
                        <p className="my-[10px] text-medium font-medium text-dark-gray">
                            {category.title}
                        </p>
                    </div>

                    <div className="flex justify-between gap-6">
                        <CardDate date={category.createdAt} />
                        <div className="flex gap-3 mr-[80px]">
                            <img
                                onClick={e => {
                                    e.stopPropagation();
                                    onUpdate(category);
                                }}
                                src={editOn}
                                className="w-10 cursor-pointer"
                                alt="Modifier"
                                role="button"
                            />
                            <img
                                onClick={e => {
                                    e.stopPropagation();
                                    onArchive(category);
                                }}
                                src={trashSVG}
                                className="w-10 cursor-pointer"
                                alt="Archiver"
                                role="button"
                            />
                        </div>
                        <button type="button" className="card-expanded" aria-expanded={isOpen} />
                    </div>
                </div>

                {isOpen && (
                    <div className="flex flex-col items-center gap-6 bg-light-gray px-6 py-6">
                        <IconButton
                            icon={addCircleIcon}
                            label="Créer une publication"
                            onClick={() => setIsCreatePublication(true)}
                        />
                        <div className={'w-full flex flex-col gap-3 items-center justify-center'}>
                            {isCreatePublication && (
                                <CategoryPublicationForm
                                    publication={Option.none()}
                                    categoryId={category.id}
                                    onPublicationSaved={() => {
                                        onRefreshPublications(category.id);
                                        setIsCreatePublication(false);
                                    }}
                                />
                            )}
                            {publicationsLoadError
                                .map(message => (
                                    <p
                                        className="w-full rounded-2xl bg-red-50 px-3 py-2 text-center text-small text-red-800"
                                        role="alert"
                                    >
                                        {message}
                                    </p>
                                ))
                                .toNull()}
                            {loadingCategoryPublication && (
                                <div className="relative h-[50px] w-[50px]">
                                    <Spinner loading={true} />
                                </div>
                            )}
                            {!loadingCategoryPublication &&
                                publicationsLoadError.isEmpty() &&
                                publications
                                    .map(items =>
                                        items.map(publication => (
                                            <CategoryPublicationCard
                                                key={publication.id}
                                                publication={publication}
                                                categoryId={category.id}
                                                onPublicationSaved={() =>
                                                    onRefreshPublications(category.id)
                                                }
                                            />
                                        )),
                                    )
                                    .toNull()}
                        </div>
                    </div>
                )}
            </li>
        );
    },
);
